import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader } from 'lucide-react';
import Footer from '../components/Footer';
import Logo from '../components/Logo';
import { formatPrice } from '../utils/priceFormatter';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const session_id = searchParams.get('session_id');

  useEffect(() => {
    if (!session_id) {
      navigate('/');
      return;
    }
    
    pollPaymentStatus();
  }, [session_id]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setError('Le délai de vérification du paiement a expiré. Veuillez vérifier votre email pour confirmation.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/stripe/checkout-status/${session_id}`);
      
      if (!response.ok) {
        throw new Error('Erreur lors de la vérification du statut');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentStatus(data);
        setLoading(false);
        return;
      } else if (data.status === 'expired') {
        setError('La session de paiement a expiré. Veuillez réessayer.');
        setLoading(false);
        return;
      }

      // If still pending, continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError('Erreur lors de la vérification du paiement. Veuillez réessayer.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Logo showTagline={true} clickable={true} />
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          {loading ? (
            <>
              <Loader className="w-16 h-16 mx-auto mb-4 text-celadon animate-spin" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Vérification du paiement...</h2>
              <p className="text-gray-600">Veuillez patienter pendant que nous confirmons votre paiement.</p>
            </>
          ) : error ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-3xl">✗</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={() => navigate('/commande')}
                className="px-6 py-3 bg-celadon text-white rounded hover:bg-celadon-light transition-colors"
              >
                Retour à la commande
              </button>
            </>
          ) : paymentStatus ? (
            <>
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-celadon" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Paiement réussi !</h2>
              <p className="text-gray-600 mb-4">Merci pour votre commande.</p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Montant payé:</span>
                  <span className="text-sm font-bold">{formatPrice(paymentStatus.amount_total / 100)} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Statut:</span>
                  <span className="text-sm font-bold text-celadon">Payé</span>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Vous recevrez un email de confirmation avec les détails de votre commande.
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => navigate('/')}
                  className="w-full px-6 py-3 bg-celadon text-white rounded hover:bg-celadon-light transition-colors"
                >
                  Retour à l'accueil
                </button>
                <button
                  onClick={() => navigate('/mes-commandes')}
                  className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                >
                  Voir mes commandes
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
